<template>
  <div class="min-w-0">
    <aside class="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-slate-800 bg-slate-950 text-white lg:flex">
      <div class="flex h-20 items-center border-b border-slate-800 px-6">
        <router-link to="/dashboard" class="flex min-w-0 items-center gap-3">
          <span class="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-blue-600 text-lg shadow-lg shadow-blue-950/40">
            <i class="fas fa-cube"></i>
          </span>
          <span class="min-w-0">
            <span class="block truncate text-base font-bold tracking-wide">轻舟魔方</span>
            <span class="block truncate text-xs text-slate-400">机构授权管理中心</span>
          </span>
        </router-link>
      </div>

      <nav class="qz-sidebar-scroll flex-1 overflow-y-auto px-4 py-5">
        <div v-for="group in visibleGroups" :key="group.label" class="mb-6">
          <div class="mb-2 px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            {{ group.label }}
          </div>
          <div class="space-y-1">
            <router-link
              v-for="item in group.items"
              :key="item.to"
              :to="item.to"
              class="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors"
              :class="isActive(item) ? 'bg-blue-600 text-white shadow-md shadow-blue-950/30' : 'text-blue-50/80 hover:bg-slate-900 hover:text-white'"
            >
              <span class="flex h-8 w-8 items-center justify-center rounded-lg" :class="isActive(item) ? 'bg-white/10' : 'bg-slate-900 text-blue-100/70 group-hover:text-white'">
                <i :class="item.icon"></i>
              </span>
              <span>{{ item.label }}</span>
            </router-link>
          </div>
        </div>
      </nav>

      <div class="border-t border-slate-800 p-4">
        <div class="flex items-center gap-3 rounded-xl bg-slate-900 px-3 py-3">
          <span class="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-blue-500/20 font-semibold text-blue-300">
            {{ initials }}
          </span>
          <div class="min-w-0 flex-1">
            <div class="truncate text-sm font-semibold text-white">{{ displayName }}</div>
            <div class="mt-0.5 text-xs text-slate-400">{{ roleText }}</div>
          </div>
          <button class="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white" title="退出登录" @click="logout">
            <i class="fas fa-sign-out-alt"></i>
          </button>
        </div>
      </div>
    </aside>

    <div
      v-if="mobileMenuOpen"
      class="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm lg:hidden"
      @click="closeMobileMenu"
    ></div>
    <aside
      class="fixed inset-y-0 left-0 z-[60] flex w-[88vw] max-w-[320px] flex-col overflow-hidden bg-slate-950 text-white shadow-2xl transition-transform duration-200 lg:hidden"
      :class="mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'"
    >
      <div class="flex h-16 items-center justify-between border-b border-slate-800 px-5">
        <router-link to="/dashboard" class="flex items-center gap-3" @click="closeMobileMenu">
          <span class="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600"><i class="fas fa-cube"></i></span>
          <span>
            <span class="block text-sm font-bold">轻舟魔方</span>
            <span class="block text-[11px] text-slate-400">机构授权管理中心</span>
          </span>
        </router-link>
        <button class="rounded-lg p-2 text-slate-400 hover:bg-slate-900 hover:text-white" aria-label="关闭导航" @click="closeMobileMenu">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <nav class="qz-sidebar-scroll flex-1 overflow-y-auto px-4 py-5">
        <div v-for="group in visibleGroups" :key="`mobile-${group.label}`" class="mb-6">
          <div class="mb-2 px-3 text-[11px] font-semibold tracking-[0.16em] text-slate-500">{{ group.label }}</div>
          <div class="space-y-1">
            <router-link
              v-for="item in group.items"
              :key="item.to"
              :to="item.to"
              class="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium"
              :class="isActive(item) ? 'bg-blue-600 text-white' : 'text-blue-50/80 hover:bg-slate-900 hover:text-white'"
              @click="closeMobileMenu"
            >
              <span class="flex h-8 w-8 items-center justify-center rounded-lg bg-white/5"><i :class="item.icon"></i></span>
              {{ item.label }}
            </router-link>
          </div>
        </div>
      </nav>
      <div class="border-t border-slate-800 p-4">
        <div class="flex items-center gap-3">
          <span class="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/20 font-semibold text-blue-300">{{ initials }}</span>
          <div class="min-w-0 flex-1">
            <div class="truncate text-sm font-semibold">{{ displayName }}</div>
            <div class="text-xs text-slate-400">{{ roleText }}</div>
          </div>
          <button class="rounded-lg p-2 text-slate-400 hover:bg-slate-900 hover:text-white" @click="logout"><i class="fas fa-sign-out-alt"></i></button>
        </div>
      </div>
    </aside>

    <header class="fixed left-0 right-0 top-0 z-30 flex h-16 min-w-0 items-center overflow-hidden border-b border-slate-200 bg-white/95 px-3 shadow-sm backdrop-blur sm:px-5 lg:left-64 lg:h-20 lg:px-7">
      <button class="mr-2 flex h-10 w-10 flex-none items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 sm:mr-3 lg:hidden" aria-label="打开导航" @click="mobileMenuOpen = true">
        <i class="fas fa-bars text-lg"></i>
      </button>
      <div class="min-w-0 flex-1">
        <h1 class="truncate text-base font-bold text-slate-900 sm:text-lg lg:text-xl">{{ pageTitle }}</h1>
        <p class="mt-0.5 hidden text-xs text-slate-500 sm:block">{{ pageSubtitle }}</p>
      </div>
      <div class="ml-2 flex flex-none items-center gap-2 sm:ml-4 sm:gap-3">
        <div v-if="hasWalletBalance" class="hidden rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-right sm:block">
          <div class="text-[11px] text-slate-500">可用 AI 额度</div>
          <div class="text-sm font-bold text-emerald-600">¥{{ walletBalance }}</div>
        </div>
        <div class="hidden text-right md:block">
          <div class="max-w-40 truncate text-sm font-semibold text-slate-800">{{ displayName }}</div>
          <div class="text-xs text-slate-500">{{ roleText }}</div>
        </div>
        <span class="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-blue-50 text-sm font-semibold text-blue-700 ring-1 ring-blue-100 sm:h-10 sm:w-10">{{ initials }}</span>
      </div>
    </header>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import api from '../services/api';

const normalizeRole = (role) => role === 'admin' ? 'super_admin' : role;

export default {
  name: 'Navbar',
  props: {
    currentUser: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['signed-out'],
  setup(props, { emit }) {
    const route = useRoute();
    const router = useRouter();
    const mobileMenuOpen = ref(false);
    const localUser = ref({});

    const readStoredUser = () => {
      try {
        localUser.value = JSON.parse(localStorage.getItem('user') || '{}');
      } catch (_) {
        localUser.value = {};
      }
    };

    const user = computed(() => Object.keys(props.currentUser || {}).length ? props.currentUser : localUser.value);
    const role = computed(() => normalizeRole(user.value?.role));
    const isSuperAdmin = computed(() => role.value === 'super_admin');
    const displayName = computed(() => user.value?.display_name || user.value?.name || user.value?.username || '管理员');
    const roleText = computed(() => isSuperAdmin.value ? '超级管理员' : role.value === 'teacher_admin' ? '老师管理员' : '管理员');
    const initials = computed(() => displayName.value.toString().trim().slice(0, 2).toUpperCase());
    const rawWalletBalance = computed(() =>
      user.value?.wallet?.balance_yuan ??
      user.value?.ai_wallet?.balance_yuan ??
      user.value?.available_balance_yuan ??
      user.value?.balance_yuan
    );
    const hasWalletBalance = computed(() => rawWalletBalance.value !== undefined && rawWalletBalance.value !== null && rawWalletBalance.value !== '');
    const walletBalance = computed(() => {
      const value = Number(rawWalletBalance.value || 0);
      return Number.isFinite(value) ? value.toFixed(2) : '0.00';
    });

    const groups = [
      {
        label: '工作空间',
        items: [
          { to: '/dashboard', label: '工作台', icon: 'fas fa-chart-pie' },
          { to: '/organization', label: '组织与学员', icon: 'fas fa-users' }
        ]
      },
      {
        label: '学员与授权',
        items: [
          { to: '/new_auths', label: '学员授权', icon: 'fas fa-key' },
          { to: '/activation_codes', label: '激活码管理', icon: 'fas fa-qrcode' },
          { to: '/viewer_cloud_accounts', label: '学员云空间', icon: 'fas fa-database', roles: ['super_admin', 'teacher_admin'] },
          { to: '/viewer_deliveries', label: '看图交付', icon: 'fas fa-cloud-arrow-up', roles: ['super_admin', 'teacher_admin'] }
        ]
      },
      {
        label: '安全与审计',
        items: [
          { to: '/security_audit', label: '安全审计', icon: 'fas fa-shield-halved', roles: ['super_admin'] }
        ]
      },
      {
        label: '系统管理',
        items: [
          { to: '/publish_manage', label: '发布中心', icon: 'fas fa-rocket', roles: ['super_admin'] },
          { to: '/plugin_store', label: '插件商店', icon: 'fas fa-store', roles: ['super_admin'] },
          { to: '/runtime', label: '运行状态', icon: 'fas fa-heart-pulse', roles: ['super_admin'] },
          { to: '/backup', label: '数据备份', icon: 'fas fa-database', roles: ['super_admin'] }
        ]
      }
    ];

    const visibleGroups = computed(() => groups
      .map(group => ({
        ...group,
        items: group.items.filter(item => !item.roles || item.roles.includes(role.value))
      }))
      .filter(group => group.items.length > 0));

    const pageTitle = computed(() => {
      if (route.name === 'Organization' && !isSuperAdmin.value) return '我的学员';
      return route.meta?.title || '轻舟魔方管理中心';
    });
    const pageSubtitle = computed(() => isSuperAdmin.value ? '查看全机构数据并管理老师与学员' : '管理由你负责的学员、授权与 AI 额度');

    const isActive = (item) => route.path === item.to || (item.to !== '/dashboard' && route.path.startsWith(`${item.to}/`));
    const closeMobileMenu = () => { mobileMenuOpen.value = false; };

    const logout = async () => {
      try {
        await api.logout();
        localStorage.removeItem('user');
        emit('signed-out');
        closeMobileMenu();
        await router.replace('/login');
        ElMessage.success('已退出登录');
      } catch (error) {
        console.error('Logout error:', error);
        if (error?.response?.status === 401) {
          localStorage.removeItem('user');
          emit('signed-out');
          closeMobileMenu();
          await router.replace('/login');
          ElMessage.success('已退出登录');
          return;
        }

        ElMessage.error(error?.response?.data?.error || '退出失败，请稍后重试');
      }
    };

    watch(() => route.fullPath, closeMobileMenu);

    onMounted(() => {
      readStoredUser();
      window.addEventListener('auth-state-changed', readStoredUser);
      window.addEventListener('storage', readStoredUser);
    });
    onBeforeUnmount(() => {
      window.removeEventListener('auth-state-changed', readStoredUser);
      window.removeEventListener('storage', readStoredUser);
    });

    return {
      mobileMenuOpen,
      visibleGroups,
      isSuperAdmin,
      displayName,
      roleText,
      initials,
      hasWalletBalance,
      walletBalance,
      pageTitle,
      pageSubtitle,
      isActive,
      closeMobileMenu,
      logout
    };
  }
};
</script>

<style scoped>
.qz-sidebar-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(100, 116, 139, 0.5) transparent;
  scrollbar-gutter: stable;
}

.qz-sidebar-scroll::-webkit-scrollbar {
  width: 5px;
}

.qz-sidebar-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.qz-sidebar-scroll::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(100, 116, 139, 0.48);
}

.qz-sidebar-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(148, 163, 184, 0.72);
}
</style>
